import "./index.css";
import { useMemo, useState } from "react";
import { NumberInput } from "./Input";
import { Switch } from "@/components/ui/switch";
import {
  calculateOptimalGameDpi,
  calculateOptimalWindowsCursorDpi,
  WINDOWS_CURSOR_STEPS,
} from "./dpi";
import { ExtraInfoIcon } from "./ExtraInfoIcon";
import { Results } from "./Results";

const DEFAULT_TARGETS = [
  {
    id: "god-sens-1" as const,
    description: "Optimal",
    value: 0.02291165,
    enabled: true,
  },
  {
    id: "god-sens-2" as const,
    description: "Optimal",
    value: 0.058765005,
    enabled: true,
  },
  {
    id: "god-sens-no-105" as const,
    description: "No 105° degree boat look",
    extraInfo:
      "If you look more than 105° to the side while in a boat, you must enter and exit a boat again to realign",
    value: 0.07446537,
    enabled: false,
  },
];

export function App() {
  const [currentSens, setCurrentSens] = useState<number | null>(null);
  const [currentDpi, setCurrentDpi] = useState<number | null>(null);

  const [targets, setTargets] = useState(DEFAULT_TARGETS);
  const [windowsCursorSpeed, setWindowsCursorSpeed] = useState(10);

  const currentEdpi = useMemo(
    () => (currentSens && currentDpi ? currentSens * currentDpi : null),
    [currentSens, currentDpi],
  );
  const currentWindowsCursorEdpi = useMemo(() => {
    if (currentDpi === null) return null;
    const modifier =
      WINDOWS_CURSOR_STEPS[windowsCursorSpeed - 1] ??
      WINDOWS_CURSOR_STEPS[WINDOWS_CURSOR_STEPS.length - 1]!;
    return currentDpi * modifier;
  }, [currentDpi, windowsCursorSpeed]);

  const [resultsTab, setResultsTab] = useState<"game-edpi" | "windows-cursor">(
    "game-edpi",
  );

  const optimalResults = useMemo(() => {
    if (!currentEdpi || !currentWindowsCursorEdpi) return null;

    const cursorResults = targets
      .filter((t) => t.enabled)
      .map((t) => ({
        target: t,
        result: calculateOptimalWindowsCursorDpi({
          idealGameEdpi: currentEdpi,
          originalCursorEdpi: currentWindowsCursorEdpi,
          targetSens: t.value,
        }),
      }))
      .sort((a, b) => a.result.game.errorPct - b.result.game.errorPct);

    const gameResults = targets
      .filter((t) => t.enabled)
      .map((t) => ({
        target: t,
        result: calculateOptimalGameDpi({
          originalEdpi: currentEdpi,
          originalCursorEdpi: currentWindowsCursorEdpi,
          targetSens: t.value,
        }),
      }))
      .sort(
        (a, b) =>
          a.result.windowsCursor.errorPct - b.result.windowsCursor.errorPct,
      );

    return {
      cursorResults,
      gameResults,
    };
  }, [currentSens, currentDpi, targets, currentEdpi, currentWindowsCursorEdpi]);

  return (
    <div className="min-w-[320px] min-h-screen w-full m-0 grid place-items-center">
      <div className="w-full max-w-xl mx-auto p-6 space-y-6">
        <header className="text-center">
          <h1 className="text-2xl font-bold tracking-tight text-white">
            Boat Eye God Sensitivity Calculator
          </h1>
          <p className="text-sm text-white/60 mt-1">
            Match your feel across different in-game sensitivities and DPIs
          </p>
        </header>

        <section className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-4">
          <h2 className="text-sm font-semibold text-white/80 uppercase tracking-wider">
            Your current setup
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-white/60">
                In-game sensitivity (from options.txt)
              </span>
              <div className="pt-2">
                <NumberInput
                  onChange={setCurrentSens}
                  // defaultValue={currentSens}
                  min={0}
                  max={1}
                />
              </div>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-white/60">Current DPI</span>
              <div className="pt-2">
                <NumberInput onChange={setCurrentDpi} min={1} max={25600} />
              </div>
            </label>
            <label className="flex flex-col gap-1 col-span-2">
              <span className="text-xs text-white/60">
                Windows cursor speed
              </span>
              <div className="pt-2 flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={20}
                  value={windowsCursorSpeed}
                  onChange={(e) =>
                    setWindowsCursorSpeed(Number(e.currentTarget.value))
                  }
                  className="flex-1 h-2 rounded-full appearance-none bg-white/20 cursor-pointer accent-emerald-500 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-runnable-track]:h-2 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer"
                />
                <span className="text-sm text-white/70 tabular-nums w-6">
                  {windowsCursorSpeed}
                </span>
              </div>
            </label>
          </div>
          <div className="flex justify-between">
            <span className="text-white/50 text-sm">
              EDPI: {currentEdpi?.toFixed(4)}
            </span>
            <span className="text-white/50 text-sm">
              Windows Cursor EDPI: {currentWindowsCursorEdpi}
            </span>
          </div>
        </section>

        <section className="rounded-xl bg-white/5 border border-white/10 p-4 space-y-4">
          <h2 className="text-sm font-semibold text-white/80 uppercase tracking-wider">
            Target sensitivities
          </h2>
          <p className="text-xs text-white/50">
            Toggle and set the in-game sensitivities you want presets for. The
            calculator will suggest the DPI to use for each so your aim feels
            the same.
          </p>
          <ul className="space-y-3">
            {targets.map((t) => (
              <li
                key={t.id}
                className="flex items-center gap-3 rounded-lg bg-white/5 p-3 border border-white/10"
              >
                <Switch
                  checked={t.enabled}
                  onCheckedChange={(checked) =>
                    setTargets((prev) =>
                      prev.map((target) =>
                        target.id === t.id
                          ? { ...target, enabled: checked }
                          : target,
                      ),
                    )
                  }
                  className="flex-shrink-0 data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/20"
                  aria-label={t.enabled ? "Disable" : "Enable"}
                />
                <label className="flex-1 flex items-center gap-2">
                  <span className="text-white/80">{t.value}</span>
                  <span className="text-white/50 text-sm">{t.description}</span>
                  {"extraInfo" in t && t.extraInfo && (
                    <ExtraInfoIcon extraInfo={t.extraInfo} />
                  )}
                </label>
              </li>
            ))}
          </ul>
        </section>
        {optimalResults !== null &&
          (optimalResults.gameResults.length > 0 ||
            optimalResults.cursorResults.length > 0) && (
            <Results
              optimalResults={optimalResults}
              resultsTab={resultsTab}
              setResultsTab={setResultsTab}
              gameDescription="DPI chosen so in-game feel matches; cursor speed may differ."
              cursorDescription="DPI + Windows cursor step chosen so cursor speed matches; in-game feel may differ."
            />
          )}
      </div>
    </div>
  );
}

export default App;
